import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CART, PRODUCTS } from '../../../routes/paths';
import { useUnit } from 'effector-react';
import { $cartState } from '../../../store/models/cart';
import logo from "../../../assets/images/logo.png";
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';

const Header = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { items } = useUnit($cartState);
  const cartItemsCount = items.reduce((acc, item) => acc + item.quantity, 0);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-brand-beige/50 bg-background-dark/80 backdrop-blur-sm">
      <div className="container flex h-20 items-center justify-between">
        <Link to="/" className="flex items-center gap-4" onClick={closeMenu}>
          <div className="text-primary size-8">
            <img src={logo} alt="Freshgrass" />
          </div>
          <h2 className="text-xl font-bold text-white">Fresh Grass</h2>
        </Link>
        <nav className="hidden items-center gap-9 lg:flex">
          <Link className="text-sm text-white font-medium hover:text-primary" to="/#how-it-works">{t('header.howItWorks')}</Link>
          <Link className="text-sm text-white font-medium hover:text-primary" to="/#benefits">{t('header.benefits')}</Link>
          <Link className="text-sm text-white font-medium hover:text-primary" to="/#our-story">{t('header.ourStory')}</Link>
          <Link className="text-sm text-white font-medium hover:text-primary" to="/#faq">{t('header.faq')}</Link>
        </nav>
        <div className="flex items-center gap-4">
          <Link to={CART} className="flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-white text-brand-deep-green border border-brand-deep-green text-sm font-bold leading-normal tracking-wide transition-colors hover:bg-brand-beige">
            <span className="truncate">{t('header.cart')} ({cartItemsCount})</span>
          </Link>
          <Link to={PRODUCTS} className="hidden lg:flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-brand-deep-green text-white text-sm font-bold leading-normal tracking-wide transition-colors hover:bg-primary">
            <span className="truncate">{t('header.buyNow')}</span>
          </Link>
          <button className="p-2 lg:hidden" onClick={toggleMenu}>
            <span className="material-symbols-outlined text-white">menu</span>
          </button>
          <LanguageSwitcher />
        </div>
      </div>

      {/* Mobile Menu Sidebar */}
      <div
        className={`fixed inset-0 z-50 bg-background-light transform transition-transform duration-300 ease-in-out lg:hidden ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        style={{ width: '100vw', height: '100vh' }}
      >
        <div className="flex flex-col h-full p-6">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="flex items-center gap-4" onClick={closeMenu}>
              <div className="text-brand-deep-green size-8">
                <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                  <path clipRule="evenodd" d="M39.475 21.6262C40.358 21.4363 40.6863 21.5589 40.7581 21.5934C40.7876 21.655 40.8547 21.857 40.8082 22.3336C40.7408 23.0255 40.4502 24.0046 39.8572 25.2301C38.6799 27.6631 36.5085 30.6631 33.5858 33.5858C30.6631 36.5085 27.6632 38.6799 25.2301 39.8572C24.0046 40.4502 23.0255 40.7407 22.3336 40.8082C21.8571 40.8547 21.6551 40.7875 21.5934 40.7581C21.5589 40.6863 21.4363 40.358 21.6262 39.475C21.8562 38.4054 22.4689 36.9657 23.5038 35.2817C24.7575 33.2417 26.5497 30.9744 28.7621 28.762C30.9744 26.5497 33.2417 24.7574 35.2817 23.5037C36.9657 22.4689 38.4054 21.8562 39.475 21.6262ZM4.41189 29.2403L18.7597 43.5881C19.8813 44.7097 21.4027 44.9179 22.7217 44.7893C24.0585 44.659 25.5148 44.1631 26.9723 43.4579C29.9052 42.0387 33.2618 39.5667 36.4142 36.4142C39.5667 33.2618 42.0387 29.9052 43.4579 26.9723C44.1631 25.5148 44.659 24.0585 44.7893 22.7217C44.9179 21.4027 44.7097 19.8813 43.5881 18.7597L29.2403 4.41187C27.8527 3.02428 25.8765 3.02573 24.2861 3.36776C22.6081 3.72863 20.7334 4.58419 18.8396 5.74801C16.4978 7.18716 13.9881 9.18353 11.5858 11.5858C9.18354 13.988 7.18717 16.4978 5.74802 18.8396C4.58421 20.7334 3.72865 22.6081 3.36778 24.2861C3.02574 25.8765 3.02429 27.8527 4.41189 29.2403Z" fill="currentColor" fillRule="evenodd"></path>
                </svg>
              </div>
              <h2 className="text-xl font-bold text-brand-deep-green">Fresh Grass</h2>
            </Link>
            <button className="p-2" onClick={closeMenu}>
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
          <nav className="flex flex-col gap-6">
            <Link className="text-lg font-medium hover:text-primary text-center" to="/" onClick={closeMenu}>{t('header.home')}</Link>
            <Link className="text-lg font-medium hover:text-primary text-center" to="/#how-it-works" onClick={closeMenu}>{t('header.howItWorks')}</Link>
            <Link className="text-lg font-medium hover:text-primary text-center" to="/#benefits" onClick={closeMenu}>{t('header.benefits')}</Link>
            <Link className="text-lg font-medium hover:text-primary text-center" to="/#our-story" onClick={closeMenu}>{t('header.ourStory')}</Link>
            <Link className="text-lg font-medium hover:text-primary text-center" to="/#faq" onClick={closeMenu}>{t('header.faq')}</Link>
            <Link to={CART} className="max-w-[300px] mx-auto flex w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-4 bg-white text-brand-deep-green border border-brand-deep-green text-lg font-bold leading-normal tracking-wide transition-colors hover:bg-brand-beige mt-4" onClick={closeMenu}>
              <span className="truncate">{t('header.cart')} ({cartItemsCount})</span>
            </Link>
            <Link to={PRODUCTS} className="max-w-[300px] mx-auto flex w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-4 bg-brand-deep-green text-white text-lg font-bold leading-normal tracking-wide transition-colors hover:bg-primary mt-4" onClick={closeMenu}>
              <span className="truncate">{t('header.buyNow')}</span>
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
